// TODO: Lodash should no longer be used here
// eslint-disable-next-line no-restricted-imports
import { defaultsDeep, find, isEmpty, omit, pick } from 'lodash';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import { InputOnChangeData } from 'semantic-ui-react';

// Use react hook form instead of semantic-ui-react
import { Controller, useForm } from 'react-hook-form';

// WS-common-base-ui imports
import { Button, Input, Label } from '@wonderschool/common-base-ui';

// Import components
import { logError } from '../../rollbar';
import LocationPicker from '../Locations/LocationPicker';
import InlineError from '../Messages/InlineError';
import ShowError from '../Messages/ShowError';
import { DeleteRoomButton } from '../Rooms/DeleteRoomButton';

// Import actions
import { Room } from '@wonderschool/common-base-types';
import {
  CreateRoomInput,
  UpdateRoomInput,
  useRoomCreateMutation,
  useRoomUpdateMutation,
} from '@wonderschool/common-food-program-data-access';
import { ageRangeMonths, ageRangeYears } from '../../config';
import { useContacts } from '../../contacts';
import { roomAdded, roomSelectionCleared, roomUpdated } from '../../redux/actions/roomActions';
import { getOrganizationSetupState } from '../../redux/reducers/organizations';
import { RootReducerState } from '../../redux/reducers/rootReducer';
import { useStudents } from '../../students/studentsHooks';

type Errors = {
  [K in keyof Omit<Room, 'id' | 'createdBy' | 'createdAt'>]?: string;
};

export const INITIAL_ROOM: Room = {
  id: '',
  name: '',
  location: '',
  ratio: '',
  ageRange: {
    from: {
      months: ageRangeMonths[0].value,
      years: ageRangeYears[0].value,
    },
    to: {
      months: ageRangeMonths[0].value,
      years: ageRangeYears[0].value,
    },
  },
  enabled: true,
  usesExactAgeRange: false,
  createdBy: undefined,
  createdAt: '',
  organization: '',
};

export interface ReduxStateProps {
  user: {
    uid: string;
    email?: string;
  };
  currentOrganization: {
    id: string;
  };
  rooms: {
    selectedRoom: {
      id: string;
    };
    list: {
      id: string;
    }[];
  };
  students: {
    selectedStudent: {
      id: string;
    };
    list: {
      id: string;
      rooms: string[];
    }[];
  };
}

type RoomFormProps = {
  onSubmit: (data: Room) => void;
  onCancel: () => void;
  newRoom: boolean;
  unarchivedRoomsCount: number;
};

export function RoomForm({ onSubmit, onCancel, newRoom, unarchivedRoomsCount }: RoomFormProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Replace mapStateToProps with useSelector
  const rooms = useSelector((state: RootReducerState) => state.rooms);
  const { organizationId } = useSelector((state: RootReducerState) => getOrganizationSetupState(state.organizations));

  // Students and staff
  const students = useStudents();
  const { staffContacts } = useContacts();

  const [roomCreateMutation] = useRoomCreateMutation();
  const [roomUpdateMutation] = useRoomUpdateMutation();

  // Convert state to useState hooks
  const [data, setData] = useState<Room>(INITIAL_ROOM);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});

  // Disable if no data
  const isSubmitDisabled = !data.name?.trim() || !data.location?.trim() || !data.ageRange;

  // Check if the room has students or staff (notify delete dialog if so)
  const roomHasStudent = students.list?.some((student) => {
    return student.rooms.includes(data.id);
  });
  const roomHasStaff = staffContacts?.some((staff) => {
    return staff.rooms.includes(data.id);
  });
  const hasStudentsOrStaff = roomHasStudent || roomHasStaff;

  // Disable delete if only 1 room
  const onlyOneRoom = unarchivedRoomsCount === 1;

  const { control } = useForm<Room>({
    defaultValues: INITIAL_ROOM,
  });

  useEffect(() => {
    return () => {
      dispatch(roomSelectionCleared());
    };
  }, [dispatch]);

  // Replace getDerivedStateFromProps
  useEffect(() => {
    if (!isEmpty(rooms.selectedRoom?.id) && data.id !== rooms.selectedRoom?.id) {
      setData(ensureCorrectRoomValues(rooms.selectedRoom));
    }
  }, [rooms.selectedRoom, data.id]);

  // Convert class methods to function declarations
  const onChange = (e: ChangeEvent<HTMLInputElement>, { name, value }: InputOnChangeData) => {
    setData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => omit(prev, name));
  };

  const onDateRangeChange = (e: ChangeEvent<HTMLInputElement>, { name, value }: InputOnChangeData) => {
    const [bound, unit] = name.split('-'); // 'from'|'to', 'years'|'months'
    const parsed = value === '' || value == null ? undefined : Number(value);

    setData((prev) => ({
      ...prev,
      ageRange: {
        ...prev.ageRange,
        [bound]: {
          ...prev.ageRange[bound],
          [unit]: parsed,
        },
      },
    }));
    setErrors((prev) => omit(prev, 'ageRange'));
  };

  const validate = (data) => {
    const errors: Errors = {};

    const { value: fromYears } = find(ageRangeYears, (o) => o.value === data.ageRange?.from?.years) ?? {
      value: INITIAL_ROOM.ageRange?.from?.years,
    };
    const { value: fromMonths } = find(ageRangeMonths, (o) => o.value === data.ageRange?.from?.months) ?? {
      value: INITIAL_ROOM.ageRange?.from?.months,
    };
    const { value: toYears } = find(ageRangeYears, (o) => o.value === data.ageRange?.to?.years) ?? {
      value: INITIAL_ROOM.ageRange?.to?.years,
    };
    const { value: toMonths } = find(ageRangeMonths, (o) => o.value === data.ageRange?.to?.months) ?? {
      value: INITIAL_ROOM.ageRange?.to?.months,
    };

    if (!data.name) errors.name = t('errors.validation.roomNameRequired');
    if (!data.location) errors.location = t('errors.validation.roomLocationRequired');
    if (data.ratio && data.ratio < 1) errors.ratio = t('errors.validation.studentTeacherRatio');

    // More elegant comparison: raw total months
    const fromTotalMonths = fromYears * 12 + fromMonths;
    const toTotalMonths = toYears * 12 + toMonths;
    if (fromTotalMonths > toTotalMonths) {
      errors.ageRange = t('errors.validation.ageRange');
    }

    return errors;
  };

  const handleSubmitError = (error: any) => {
    if (error?.message?.includes('duplicate')) {
      setErrors({ name: t('errors.validation.roomNameInvalid') });
    } else {
      setErrors({ name: t('errors.unknown') });
      logError('roomUpdateOrCreateMutation error', error);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const validationErrors = validate(data);
    setErrors(validationErrors);

    if (isEmpty(validationErrors)) {
      const { id, location, ...rest } = data;

      try {
        if (id) {
          const updateInput: UpdateRoomInput = {
            firebaseId: id,
            locationIdentifier: { firebaseId: location },
            ...pick(rest, ['name', 'enabled', 'ageRange', 'ratio', 'usesExactAgeRange']),
          };

          await roomUpdateMutation({
            variables: {
              input: updateInput,
            },
            onCompleted: () => {
              dispatch(roomUpdated(data));
              onSubmit(data);
            },
            onError: (error) => {
              handleSubmitError(error);
            },
          });
        } else {
          const createInput: CreateRoomInput = {
            locationIdentifier: { firebaseId: location },
            ...pick(rest, ['name', 'enabled', 'ageRange', 'ratio', 'usesExactAgeRange']),
          };

          await roomCreateMutation({
            variables: {
              input: createInput,
            },
            onCompleted: () => {
              dispatch(roomAdded(data));
              onSubmit(data);
            },
            onError: (error) => {
              handleSubmitError(error);
            },
          });
        }
      } catch (error: unknown) {
        handleSubmitError(error);
      } finally {
        setLoading(false);
      }
    }
    setLoading(false);
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    dispatch(roomSelectionCleared());
    onCancel();
  };

  const translatedAgeMonthOptions = ageRangeMonths.map((x) => {
    x.text = t(x.text);
    return x;
  });
  const translatedAgeYearOptions = ageRangeYears.map((x) => {
    x.text = t(x.text);
    return x;
  });

  return (
    <div className="flex min-w-96 max-w-96 flex-col">
      <form onSubmit={handleSubmit} id="room-form" noValidate data-testid="room-form" className="flex flex-col gap-8">
        <div className="flex max-w-96">
          <ShowError errors={errors} />
        </div>

        {/* Name */}
        <div className="form-field" data-testid="room-field">
          <Label htmlFor="name" required extraClasses="mb-2 font-bold">
            {t('common.name')}
          </Label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                type="text"
                id="name"
                className="w-full rounded-md"
                placeholder={t('Room name')}
                required
                value={data.name ?? ''}
                onChange={(e) => {
                  const v = (e.target as HTMLInputElement).value;
                  field.onChange(v);
                  onChange(e as any, { name: 'name', value: v });
                }}
                onBlur={field.onBlur}
                data-testid="room-name"
              />
            )}
          />
          {errors.name && <InlineError text={errors.name} data-testid="name-error" />}
        </div>

        {/* Location */}
        <div className="form-field" data-testid="location-field">
          <Label htmlFor="location" required extraClasses="mb-2 font-bold">
            {t('Location')}
          </Label>
          <Controller
            name="location"
            control={control}
            render={({ field }) => (
              <LocationPicker
                id="location"
                name="location"
                className="w-full rounded-md"
                value={data.location}
                placeholder={t('Select location')}
                data-testid="room-location"
                onChange={(e: any, d: any) => {
                  const v = d?.value;
                  field.onChange(v);
                  onChange(e, { name: 'location', value: v });
                }}
              />
            )}
          />
          {errors.location && <InlineError text={errors.location} data-testid="location-error" />}
        </div>

        {/* Ratio */}
        <div className="form-field" data-testid="ratio-field">
          <Label htmlFor="ratio" extraClasses="mb-2 font-bold">
            {t('common.studentTeacherRatio')}
          </Label>

          {/* Wild hack to get :1 to render cleanly with the input: render two inputs, one is a ghost with :1, the other is the real input */}
          <Controller
            name="ratio"
            control={control}
            render={({ field }) => (
              <div className="relative">
                {/* Ghost display layer (behind): shows `${ratio}:1` or placeholder */}
                <input
                  tabIndex={-1}
                  aria-hidden="true"
                  readOnly
                  className="pointer-events-none absolute inset-0 z-0 w-full select-none rounded-md px-3 py-2"
                  value={data.ratio ? `${data.ratio}:1` : ''}
                  placeholder={t('Example: 4:1')}
                />

                {/* Real input (on top): captures value, but hides its own text so ghost is visible */}
                <input
                  type="number"
                  inputMode="numeric" // brings up numeric keypad on mobile
                  pattern="[0-9]*"
                  id="ratio"
                  className="relative z-10 w-full rounded-md bg-transparent px-3 py-2 text-transparent placeholder-transparent caret-slate-900"
                  aria-label={t('common.studentTeacherRatio')}
                  value={data.ratio ?? ''}
                  onChange={(e) => {
                    const v = (e.target as HTMLInputElement).value;
                    field.onChange(v);
                    onChange(e as any, { name: 'ratio', value: v });
                  }}
                  onBlur={field.onBlur}
                  data-testid="student-ratio"
                />
              </div>
            )}
          />

          {errors.ratio && <InlineError text={errors.ratio} data-testid="ratio-error" />}
        </div>

        {/* Age range – least age */}
        <div className="form-field" data-testid="age-field-least">
          <Label htmlFor="ageRange.from.years" required extraClasses="mb-2 font-bold">
            {t('common.studentMinAgeRange')}
          </Label>
          <div className="room-age-input-split">
            {/* From Years */}
            <Controller
              name="ageRange.from.years"
              control={control}
              render={({ field }) => (
                <select
                  name="from-years"
                  className="age-input w-full rounded-md"
                  value={data.ageRange?.from?.years || translatedAgeYearOptions[0].value}
                  onChange={(e) => {
                    const raw = (e.target as HTMLSelectElement).value;
                    const v = raw === '' ? '' : Number(raw);
                    field.onChange(v);
                    onDateRangeChange(e as any, { name: 'from-years', value: v.toString() });
                  }}
                  data-testid="room-age-from-years"
                >
                  {translatedAgeYearOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.text}
                    </option>
                  ))}
                </select>
              )}
            />
            {/* From Months */}
            <Controller
              name="ageRange.from.months"
              control={control}
              render={({ field }) => (
                <select
                  name="from-months"
                  className="age-input w-full rounded-md"
                  value={data.ageRange?.from?.months || translatedAgeMonthOptions[0].value}
                  onChange={(e) => {
                    const raw = (e.target as HTMLSelectElement).value;
                    const v = raw === '' ? '' : Number(raw);
                    field.onChange(v);
                    onDateRangeChange(e as any, { name: 'from-months', value: v.toString() });
                  }}
                  data-testid="room-age-from-months"
                >
                  {translatedAgeMonthOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.text}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>
        </div>

        {/* Age range – max age */}
        <div className="form-field" data-testid="age-field-older">
          <Label htmlFor="ageRange.to.years" required extraClasses="mb-2 font-bold">
            {t('common.studentMaxAgeRange')}
          </Label>
          <div className="room-age-input-split">
            {/* To Years */}
            <Controller
              name="ageRange.to.years"
              control={control}
              render={({ field }) => (
                <select
                  name="to-years"
                  className="age-input w-full rounded-md"
                  value={data.ageRange?.to?.years || translatedAgeYearOptions[0].value}
                  onChange={(e) => {
                    const raw = (e.target as HTMLSelectElement).value;
                    const v = raw === '' ? '' : Number(raw);
                    field.onChange(v);
                    onDateRangeChange(e as any, { name: 'to-years', value: v.toString() });
                  }}
                  data-testid="room-age-to-years"
                >
                  {translatedAgeYearOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.text}
                    </option>
                  ))}
                </select>
              )}
            />
            {/* To Months */}
            <Controller
              name="ageRange.to.months"
              control={control}
              render={({ field }) => (
                <select
                  name="to-months"
                  className="age-input w-full rounded-md"
                  value={data.ageRange?.to?.months || translatedAgeMonthOptions[0].value}
                  onChange={(e) => {
                    const raw = (e.target as HTMLSelectElement).value;
                    const v = raw === '' ? '' : Number(raw);
                    field.onChange(v);
                    onDateRangeChange(e as any, { name: 'to-months', value: v.toString() });
                  }}
                  data-testid="room-age-to-months"
                >
                  {translatedAgeMonthOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.text}
                    </option>
                  ))}
                </select>
              )}
            />
          </div>
          {errors.ageRange && <InlineError text={errors.ageRange} data-testid="age-range-error" />}
        </div>

        {/* Actions */}
        <div className="mt-4 flex gap-2">
          <Button
            extraClasses="w-full"
            primary
            type="submit"
            label={data.id ? t('common.update') : t('Save')}
            data-testid="update-btn"
            disabled={isSubmitDisabled}
            loading={loading}
          />
          <Button extraClasses="w-full" label={t('common.cancel')} onClick={handleCancel} data-testid="cancel-btn" />
        </div>
      </form>

      {/* Delete button, which also has a dialog */}
      <DeleteRoomButton
        room={data}
        organizationId={organizationId}
        hasStudentsOrStaff={hasStudentsOrStaff}
        onDeleted={() => onSubmit(data)}
        disabled={newRoom}
        onlyOneRoom={onlyOneRoom}
      />
    </div>
  );
}

export function ensureCorrectRoomValues(roomData, defaultRoom = INITIAL_ROOM) {
  // remove empty string from ageRange.to/from so that we can use a default instead
  if (roomData?.ageRange?.to === '') {
    roomData.ageRange.to = undefined;
  }
  if (roomData?.ageRange?.from === '') {
    roomData.ageRange.from = undefined;
  }
  return {
    ...defaultsDeep({}, { ...roomData }, { ...defaultRoom }),
  };
}

export default RoomForm;
